import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClinicalNotes from '@/components/ClinicalNotes';
import { getAdmissionStart } from '@/lib/note-filters';
import type { ReplitDashboardData, ReplitDocument } from '@/types/replit-dashboard';
import type { StartupPayload } from '@/types/startup';
import SectionList from './SectionList';
import { sectionHints } from './section-hints';
import SectionPage, { type SectionOverview } from './SectionPage';
import ConditionsSection from './ConditionsSection';
import MedicationsSection from './MedicationsSection';
import LabsSection from './LabsSection';
import NotesSection from './NotesSection';
import ImagingSection from './ImagingSection';
import VitalsSection from './VitalsSection';
import CareTeamSection from './CareTeamSection';
import AllergiesList from './AllergiesList';
import ProceduresList from './ProceduresList';

export type RecordsSectionId =
  | 'conditions'
  | 'medications'
  | 'notes'
  | 'labs'
  | 'imaging'
  | 'vitals'
  | 'careTeam'
  | 'allergies'
  | 'procedures';

interface RecordsPanelProps {
  data: ReplitDashboardData;
  /** Null while loading, disabled, or failed: the panel then renders record data only. */
  startup: StartupPayload | null;
  onAskQuestion: (question: string, displayText?: string) => void;
  variant: 'main' | 'rail';
  /** Rail only: promote the panel to the main view. */
  onOpenFull?: () => void;
  /** Lift the open section so drill-in survives the panel remounting (layout and tab switches). */
  openSection?: RecordsSectionId | null;
  onOpenSectionChange?: (section: RecordsSectionId | null) => void;
}

const isImagingReport = (d: ReplitDocument) => (d.noteType || '').toLowerCase() === 'diagnostic imaging report';
const isProcedureNote = (d: ReplitDocument) => (d.noteType || '').toLowerCase() === 'procedure note';

export default function RecordsPanel({ data, startup, onAskQuestion, variant, onOpenFull, openSection, onOpenSectionChange }: RecordsPanelProps) {
  const { t, i18n } = useTranslation();
  const [internalOpenSection, setInternalOpenSection] = useState<RecordsSectionId | null>(null);
  const isControlled = openSection !== undefined;
  const currentSection = isControlled ? openSection : internalOpenSection;
  const setOpenSection = (section: RecordsSectionId | null) => {
    if (!isControlled) setInternalOpenSection(section);
    onOpenSectionChange?.(section);
  };

  const { patient } = data;
  const name = patient.firstName;
  const allDocuments = data.documents || [];
  const imagingReportDocs = allDocuments.filter(isImagingReport);
  const procedureNoteDocs = allDocuments.filter(isProcedureNote);
  const clinicalNotes = allDocuments.filter((d) => !isImagingReport(d) && !isProcedureNote(d));
  const admissionStart = patient.admissionDate || getAdmissionStart(data.encounters || []);

  const labels: Record<RecordsSectionId, string> = {
    conditions: t('dashboard.conditionsAndDiagnoses'),
    medications: t('dashboard.currentMedications'),
    notes: t('dashboard.clinicalNotes'),
    labs: t('dashboard.labResults'),
    imaging: t('dashboard.imaging'),
    vitals: t('dashboard.vitalSigns'),
    careTeam: t('dashboard.careTeam'),
    allergies: t('dashboard.allergies'),
    procedures: t('dashboard.procedures'),
  };

  const sectionIds: RecordsSectionId[] = ['conditions', 'medications', 'notes', 'labs', 'imaging', 'vitals', 'careTeam'];
  if (data.allergies && data.allergies.length > 0) sectionIds.push('allergies');
  if (data.procedures && data.procedures.length > 0) sectionIds.push('procedures');

  if (!currentSection) {
    const hints = sectionHints({ ...data, documents: clinicalNotes, imagingReportDocs }, startup?.whatsNewWindow, i18n.resolvedLanguage || 'en');
    return (
      <div className="h-full overflow-y-auto">
        <SectionList
          patientFirstName={name}
          whatsNew={startup?.whatsNew}
          whatsNewWindow={startup?.whatsNewWindow}
          hints={hints}
          sections={sectionIds.map((id) => ({ id, label: labels[id] }))}
          onOpen={setOpenSection}
          onAskQuestion={onAskQuestion}
          variant={variant}
        />
      </div>
    );
  }

  const overviewFor = (titleKey: string, text: string | undefined): SectionOverview | null =>
    startup && text ? { title: t(titleKey, { name }), text, generatedAt: startup.generatedAt } : null;

  const overviews: Partial<Record<RecordsSectionId, SectionOverview | null>> = {
    conditions: overviewFor('records.overviewConditions', startup?.sections.conditions.overview),
    medications: overviewFor('records.overviewMedications', startup?.sections.medications.overview),
    labs: overviewFor('records.overviewLabs', startup?.sections.labs.overview),
    imaging: overviewFor('records.overviewImaging', startup?.sections.imaging.overview),
    notes: overviewFor('records.overviewNotes', startup?.sections.notes.overview),
    vitals: overviewFor('records.overviewVitals', startup?.sections.vitals.overview),
  };

  const explainQuestions: Record<RecordsSectionId, string> = {
    conditions: t('records.askConditions', { name }),
    medications: t('explain.currentMedications', { name }),
    notes: t('records.askNotes', { name }),
    labs: t('records.askLabs', { name }),
    imaging: t('records.askImaging', { name }),
    vitals: t('explain.vitalsAll'),
    careTeam: t('records.askCareTeam', { name }),
    allergies: t('records.askAllergies', { name }),
    procedures: t('records.askProcedures', { name }),
  };

  const renderBody = () => {
    switch (currentSection) {
      case 'conditions':
        return (
          <ConditionsSection
            conditions={data.conditions || []}
            admissionDate={admissionStart}
            items={startup?.sections.conditions.items}
            patientFirstName={name}
            onAskQuestion={onAskQuestion}
            variant={variant}
          />
        );
      case 'medications':
        return (
          <MedicationsSection
            medications={data.medications || []}
            items={startup?.sections.medications.items}
            patientFirstName={name}
            onAskQuestion={onAskQuestion}
            variant={variant}
          />
        );
      case 'notes':
        return <NotesSection notes={clinicalNotes} onAskQuestion={onAskQuestion} variant={variant} />;
      case 'labs':
        return (
          <LabsSection
            labResults={data.labResults || []}
            labs={startup?.sections.labs ?? null}
            patientFirstName={name}
            onAskQuestion={onAskQuestion}
            variant={variant}
          />
        );
      case 'imaging':
        return (
          <ImagingSection
            studies={data.imagingStudies || []}
            reportDocuments={imagingReportDocs}
            onAskQuestion={onAskQuestion}
            variant={variant}
          />
        );
      case 'vitals':
        return <VitalsSection vitalSigns={data.vitalSigns || []} onAskQuestion={onAskQuestion} variant={variant} />;
      case 'careTeam':
        return <CareTeamSection careTeam={data.careTeam || []} onAskQuestion={onAskQuestion} variant={variant} />;
      case 'allergies':
        return <AllergiesList allergies={data.allergies || []} patientFirstName={name} onAskQuestion={onAskQuestion} />;
      case 'procedures':
        return (
          <>
            <ProceduresList procedures={data.procedures || []} patientFirstName={name} onAskQuestion={onAskQuestion} />
            {procedureNoteDocs.length > 0 && (
              <div className="mt-3">
                <ClinicalNotes notes={procedureNoteDocs} enableControls={false} onAskQuestion={onAskQuestion} />
              </div>
            )}
          </>
        );
    }
  };

  return (
    <SectionPage
      key={currentSection}
      title={labels[currentSection]}
      patientFirstName={name}
      overview={overviews[currentSection]}
      explainQuestion={explainQuestions[currentSection]}
      variant={variant}
      onBack={() => setOpenSection(null)}
      onAskQuestion={onAskQuestion}
      onOpenFull={onOpenFull}
    >
      {renderBody()}
    </SectionPage>
  );
}
